import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { FaSearch, FaCircle, FaUndoAlt, FaCheckCircle, FaMoneyBillWave } from "react-icons/fa";
import Swal from "sweetalert2";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

function VendorCancelOrder() {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  // Client-side pagination matching your UI properties
  const [page, setPage] = useState(1);
  const limit = 50;

  // 🟢 Fetch all records from the order_refunds tracking ledger
  const fetchRefunds = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getAllRefunds`);
      if (res.data.success) {
        setRefunds(res.data.refunds || []);
      }
    } catch (err) {
      console.error("Fetch refunds list error:", err);
      Swal.fire("Error", "Failed to pull down target refund transaction records.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefunds();
  }, []);

  // 🟢 Handle finalizing the pending refund record state workflow
  const handleProcessRefund = async (refundId, orderId) => {
    const paddedId = String(orderId).padStart(10, '0');

    // Prompt user for reference remarks (e.g., Bank transaction reference ID)
    const { value: textRemarks } = await Swal.fire({
      title: "Complete Refund Process?",
      text: `Are you sure you want to mark Order #${paddedId} as Processed? Enter payment transaction reference numbers below:`,
      input: "text",
      inputPlaceholder: "e.g., TXN12948122, IMPS Reference, etc.",
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#10b981", // Success Green
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Confirm Settlement",
      reverseButtons: true,
    });

    if (textRemarks === undefined) return; // User pressed cancel

    try {
      setLoading(true);
      const payload = {
        refund_id: refundId,
        remarks: textRemarks || "Manual system settlement execution"
      };

      console.log("✈️ Sending payload to completeRefundProcess:", payload);

      const res = await axios.post(`${baseurl}completeRefundProcess`, payload);
      
      console.log("📥 Received execution response from server:", res.data);

      if (res.data.success) {
        Swal.fire({
          title: "Refund Processed!",
          text: `Refund tracking record for Order #${paddedId} marked as Processed successfully.`,
          icon: "success",
          confirmButtonColor: "#2874f0"
        });
        fetchRefunds(); // Re-fetch list records to mirror updated context updates
      } else {
        Swal.fire("Failed", res.data.message || "Failed to update record state.", "error");
      }
    } catch (err) {
      console.error("Error updates executing completeRefundProcess:", err);
      Swal.fire("Execution Error", err.response?.data?.message || "Internal network error occurred.", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🟢 Client side local evaluation matching your custom criteria structure array
  const filteredRefunds = React.useMemo(() => {
    return refunds.filter((r) => {
      const paddedId = String(r.raw_order_id || r.order_id).padStart(10, '0');
      const matchesSearch =
        paddedId.includes(filters.search) ||
        r.order_uuid?.toLowerCase().includes(filters.search.toLowerCase()) ||
        (r.remarks || "").toLowerCase().includes(filters.search.toLowerCase());

      const matchesStatus = filters.status === "" || r.refund_status === filters.status;

      const recordDate = new Date(r.created_at);
      const matchesFromDate = !filters.fromDate || recordDate >= new Date(filters.fromDate);
      const matchesToDate = !filters.toDate || recordDate <= new Date(filters.toDate);

      return matchesSearch && matchesStatus && matchesFromDate && matchesToDate;
    });
  }, [refunds, filters]);

  // Client-side pagination segmentation matching your grid counts properties
  const paginatedRefunds = React.useMemo(() => {
    const startIndex = (page - 1) * limit;
    return filteredRefunds.slice(startIndex, startIndex + limit);
  }, [filteredRefunds, page]);

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper bg-light">
        <div className="main-content p-3 p-lg-4">
          
          {/* Header & Quick Stats */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <div>
              <h4 className="fw-bold mb-1 text-dark">Refund Management Ledger</h4>
              <p className="text-muted small mb-0">Total of <strong>{filteredRefunds.length}</strong> refund entries matches applied filters.</p>
            </div>
            <div className="d-flex gap-2">
                <div className="stat-pill bg-white border px-3 py-2 rounded-3 shadow-sm">
                    <small className="text-muted d-block lh-1">Total Payout Volume</small>
                    <span className="fw-bold text-danger">
                      ₹{filteredRefunds.reduce((acc, curr) => acc + parseFloat(curr.refund_amount || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                </div>
                <div className="stat-pill bg-dark text-white px-3 py-2 rounded-3 shadow-sm">
                    <small className="text-white-50 d-block lh-1">Records Listed</small>
                    <span className="fw-bold">{filteredRefunds.length} Logs</span>
                </div>
            </div>
          </div>

          {/* Compact Filter Bar */}
          <div className="card border-0 shadow-sm rounded-3 mb-3">
            <div className="card-body p-3">
              <div className="row g-2 align-items-center">
                <div className="col-md-4">
                  <div className="input-group input-group-sm border rounded-2 bg-light">
                    <span className="input-group-text bg-transparent border-0"><FaSearch className="text-muted" /></span>
                    <input
                      type="text"
                      className="form-control bg-transparent border-0"
                      placeholder="Search Order ID, UUID string or remarks..."
                      value={filters.search}
                      onChange={(e) => { setPage(1); setFilters({ ...filters, search: e.target.value }); }}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select form-select-sm border-0 bg-light"
                    value={filters.status}
                    onChange={(e) => { setPage(1); setFilters({ ...filters, status: e.target.value }); }}
                  >
                    <option value="">All Statuses</option>
                    <option value="Pending">Pending</option>
                    <option value="Processed">Processed</option>
                    <option value="N/A">N/A (COD Models)</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <div className="d-flex gap-2 align-items-center">
                    <Flatpickr
                      options={{ dateFormat: "Y-m-d" }}
                      placeholder="Created From"
                      className="form-control form-control-sm border-0 bg-light text-center"
                      value={filters.fromDate}
                      onChange={(date) => { setPage(1); setFilters({ ...filters, fromDate: date[0] }); }}
                    />
                    <span className="text-muted small">to</span>
                    <Flatpickr
                      options={{ dateFormat: "Y-m-d" }}
                      placeholder="Created To"
                      className="form-control form-control-sm border-0 bg-light text-center"
                      value={filters.toDate}
                      onChange={(date) => { setPage(1); setFilters({ ...filters, toDate: date[0] }); }}
                    />
                  </div>
                </div>
                <div className="col-md-2 text-end">
                    <button className="btn btn-sm btn-outline-secondary w-100 border-0" onClick={() => setFilters({search: "", status: "", fromDate: "", toDate: ""})}>Reset Filters</button>
                </div>
              </div>
            </div>
          </div>

          {/* Refund Ledger Table Grid */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table align-middle table-hover mb-0 order-table">
                <thead className="bg-white border-bottom">
                  <tr>
                    <th className="ps-4">#</th>
                    <th>Order Association</th>
                    <th>Payment Method</th>
                    <th>Refund Value</th>
                    <th>Ledger Status</th>
                    <th>Internal Processing Remarks Log</th>
                    <th>Log Date</th>
                    <th className="text-end pe-4">Actions Execution</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="8" className="text-center py-5"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>
                  ) : paginatedRefunds.length > 0 ? (
                    paginatedRefunds.map((r, i) => {
                      const orderDisplayId = String(r.raw_order_id || r.order_id).padStart(10, '0');
                      return (
                        <tr key={r.id}>
                          <td className="ps-4 text-muted small">{(page - 1) * limit + i + 1}</td>
                          <td>
                            <div className="fw-bold text-primary mb-0 font-monospace" style={{ fontSize: '13px' }}>
                              #{orderDisplayId}
                            </div>
                            {r.order_uuid && (
                              <div className="text-muted extra-small">
                                UUID: {r.order_uuid.substring(0, 8)}...
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="payment-method-box">
                              <span className="small text-uppercase fw-bold">{r.payment_method}</span>
                            </div>
                          </td>
                          <td>
                            <div className="fw-bold small text-dark">₹{parseFloat(r.refund_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                          </td>
                          <td>
                            <span className={`status-tag ${
                              r.refund_status === "Processed" 
                                ? "status-delivered" 
                                : r.refund_status === "Pending" 
                                ? "status-pending" 
                                : "bg-secondary text-white"
                            }`}>
                              {r.refund_status}
                            </span>
                          </td>
                          <td>
                            <div className="text-muted small text-truncate" style={{ maxWidth: "240px" }} title={r.remarks}>
                              {r.remarks || <span className="text-light-muted italic text-lowercase">No reference logged</span>}
                            </div>
                          </td>
                          <td>
                            <div className="text-muted extra-small">
                              {new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </div>
                          </td>
                          <td className="text-end pe-4">
                            {r.refund_status === "Pending" ? (
                              <button
                                onClick={() => handleProcessRefund(r.id, r.raw_order_id || r.order_id)}
                                className="btn btn-sm btn-success d-inline-flex align-items-center gap-1 font-weight-bold shadow-sm"
                                style={{ fontSize: '11px', borderRadius: '6px' }}
                              >
                                <FaUndoAlt size={10} /> Settle Payout
                              </button>
                            ) : (
                              <span className="text-success small d-inline-flex align-items-center gap-1 extra-small fw-bold border border-success-subtle bg-success-subtle px-2 py-1 rounded-2">
                                <FaCheckCircle /> Finalized
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr><td colSpan="8" className="text-center py-5">No open or recorded refunds match your current display parameters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls Footer */}
            <div className="card-footer bg-white py-3">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                <span className="text-muted small">
                  Showing <strong>{filteredRefunds.length === 0 ? 0 : (page - 1) * limit + 1}</strong> to <strong>{Math.min(page * limit, filteredRefunds.length)}</strong> of {filteredRefunds.length} entries
                </span>
                <nav>
                  <ul className="pagination pagination-sm mb-0 gap-1">
                    <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                      <button className="page-link rounded-2 border-0 bg-light px-3" onClick={() => setPage(page - 1)}>Prev</button>
                    </li>
                    <li className="page-item active"><span className="page-link rounded-2 border-0 px-3">{page}</span></li>
                    <li className={`page-item ${page >= Math.ceil(filteredRefunds.length / limit) ? 'disabled' : ''}`}>
                      <button className="page-link rounded-2 border-0 bg-light px-3" onClick={() => setPage(page + 1)}>Next</button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>

        </div>
      </main>

      <style>{`
        .extra-small { font-size: 10px; line-height: 1.2; }
        .order-table thead th { font-size: 11px; text-transform: uppercase; color: #8898aa; letter-spacing: 0.5px; padding: 15px 10px; }
        .order-table tbody td { padding: 12px 10px; border-bottom: 1px solid #f6f9fc; }
        
        /* Status Tags mapping original table structures styling layout */
        .status-tag { padding: 4px 10px; border-radius: 6px; font-size: 10px; font-weight: 700; text-transform: uppercase; display: inline-block; }
        .status-delivered { background: #dcfce7; color: #15803d; }
        .status-pending { background: #fef3c7; color: #b45309; }

        .payment-method-box { background: #fff; border: 1px solid #e2e8f0; padding: 2px 6px; border-radius: 4px; color: #475569; font-size: 9px; display: inline-block; }
        .stat-pill { min-width: 160px; }
        .text-light-muted { color: #cbd5e1; }
        .italic { font-style: italic; }
      `}</style>
    </>
  );
}

export default VendorCancelOrder;